import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { 
  Gauge, Clock, Zap, BarChart3, AlertTriangle, Target, FileText,
  ArrowUpRight, ArrowDownRight, TrendingUp, TrendingDown, Info, Factory, Settings, Activity
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import PageHeader from '../../components/PageHeader';
import KPICardOperations from '../../components/KPICardOperations';
import RealVsBudgetTrajectory from '../../components/RealVsBudgetTrajectory';
import { efficiencyTrajectoryKpis, efficiencyData } from '../../data/mock/operationsData';
import { processKeyMessages } from '../../utils/iconMapping';

// ============ COMPONENTS ============

// Status Badge Component
const StatusBadge = ({ status, language }) => {
  const configs = {
    'conforme': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: { pt: 'Conforme', en: 'On Track' } },
    'acompanhamento': { bg: 'bg-blue-100', text: 'text-blue-700', label: { pt: 'Acompanhamento', en: 'Monitoring' } },
    'pressao': { bg: 'bg-amber-100', text: 'text-amber-700', label: { pt: 'Pressão', en: 'Pressure' } },
  };
  const config = configs[status] || configs['conforme'];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-semibold ${config.bg} ${config.text}`}>
      {config.label[language]}
    </span>
  );
};

// Severity Badge Component
const SeverityBadge = ({ severity, language }) => {
  const configs = {
    'high': { bg: 'bg-rose-100', text: 'text-rose-700', label: { pt: 'Relevante', en: 'Relevant' } },
    'medium': { bg: 'bg-amber-100', text: 'text-amber-700', label: { pt: 'Moderado', en: 'Moderate' } },
    'low': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: { pt: 'Limitado', en: 'Limited' } },
  };
  const config = configs[severity] || configs['medium'];
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold ${config.bg} ${config.text}`}>
      {config.label[language]}
    </span>
  );
};

// ============ MAIN COMPONENT ============
const OperationalEfficiency = ({ year, setActiveSection, flowMap }) => {
  const { language } = useLanguage();
  const is2026 = year === 2026;
  const rawData = is2026 ? efficiencyData[2026] : efficiencyData[2025];
  
  // Process data: map icon keys to actual icon components
  const data = {
    ...rawData,
    keyMessages: processKeyMessages(rawData.keyMessages),
  };

  // Dados para o painel Real vs Trajectória do Orçamento - array completo de KPIs da página
  const trajectoryKpisYear = efficiencyTrajectoryKpis[year] || [];

  // Preparar dados para export PDF
  const pageDataForExport = {
    kpis: data.kpis,
    kpiLabels: {
      disponibilidadePlanta: language === 'pt' ? 'Disponibilidade Planta' : 'Plant Availability',
      utilizacaoEfetiva: language === 'pt' ? 'Utilização Efectiva Planta' : 'Effective Plant Utilization',
      horasParagemPlanta: language === 'pt' ? 'Horas Paragem Planta' : 'Plant Downtime Hours',
      taxaTransferenciaDMS: language === 'pt' ? 'Taxa de Transferência DMS' : 'DMS Transfer Rate',
    },
    keyMessages: data.keyMessages,
    disponibilidadeUtilizacao: data.disponibilidadeUtilizacao,
    paragensTempoPerdido: data.paragensTempoPerdido,
    constrangimentos: data.constrangimentos,
    ponteFinanceira: data.ponteFinanceira,
  };

  return (
    <div className="space-y-6" data-testid="operational-efficiency">
      {/* Page Header */}
      <PageHeader
        title={language === 'pt' ? 'Eficiência Operacional' : 'Operational Efficiency'}
        period={data.period}
        subtitle={language === 'pt' 
          ? `vs Orçamento ${data.period} e comparativo com ${is2026 ? '1T25' : '2024'}`
          : `vs Budget ${data.period} and comparison with ${is2026 ? 'Q1 25' : '2024'}`}
        language={language}
        flowMap={flowMap}
        pageData={pageDataForExport}
      />

      {/* ============ MENSAGENS-CHAVE - McKinsey Premium Compact ============ */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#112240] rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-0.5 h-4 bg-gold-500 rounded-full"></div>
          <h3 className="text-white/90 font-medium text-xs uppercase tracking-[0.15em]">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Messages'}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {data.keyMessages.map((msg, idx) => {
            const MsgIcon = msg.icon;
            return (
              <div key={idx} className="bg-white/5 border border-white/10 rounded-lg p-3 hover:bg-white/8 transition-colors">
                <div className="flex items-start gap-2.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${msg.iconBg}`}>
                    <MsgIcon className={`w-4 h-4 ${msg.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-semibold text-[10px] mb-1.5 uppercase tracking-wider ${msg.titleColor}`}>
                      {msg.title[language]}
                    </h4>
                    <p className="text-slate-300/90 text-[11px] leading-relaxed">{msg.text[language]}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ============ 4 KPIs OFICIAIS ============ */}
      <div className="grid grid-cols-4 gap-4">
        <KPICardOperations 
          label={language === 'pt' ? 'Disponibilidade Planta' : 'Plant Availability'}
          value={data.kpis.disponibilidadePlanta.actual.toFixed(1)}
          unit={data.kpis.disponibilidadePlanta.unit}
          vsBudget={data.kpis.disponibilidadePlanta.variance}
          vsPrior={data.kpis.disponibilidadePlanta.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Disponibilidade da Planta' : 'Plant Availability',
            definition: language === 'pt' 
              ? 'Percentagem do tempo em que a planta está operacionalmente disponível para produção.'
              : 'Percentage of time the plant is operationally available for production.',
            relevance: language === 'pt'
              ? 'Principal indicador de fiabilidade dos activos. Cada 1% de disponibilidade representa ~3K quilates potenciais.'
              : 'Main asset reliability indicator. Each 1% availability represents ~3K potential carats.',
            currentReading: language === 'pt'
              ? `Disponibilidade de ${data.kpis.disponibilidadePlanta.actual.toFixed(1)}% indica ${data.kpis.disponibilidadePlanta.variance >= 0 ? 'boa' : 'fragilidade na'} fiabilidade operacional.`
              : `Availability of ${data.kpis.disponibilidadePlanta.actual.toFixed(1)}% indicates ${data.kpis.disponibilidadePlanta.variance >= 0 ? 'good' : 'weakness in'} operational reliability.`,
            drillDown: [
              { label: language === 'pt' ? 'Primária (Britagem)' : 'Primary (Crushing)', value: '94.2%', variance: 1.2 },
              { label: language === 'pt' ? 'Secundária (DMS)' : 'Secondary (DMS)', value: '91.5%', variance: -2.8 },
              { label: language === 'pt' ? 'Terciária (Sorting)' : 'Tertiary (Sorting)', value: '96.1%', variance: 0.5 },
            ]
          }}
        />
        <KPICardOperations 
          label={language === 'pt' ? 'Utilização Efectiva Planta' : 'Effective Plant Utilization'}
          value={data.kpis.utilizacaoEfetiva.actual.toFixed(1)}
          unit={data.kpis.utilizacaoEfetiva.unit}
          vsBudget={data.kpis.utilizacaoEfetiva.variance}
          vsPrior={data.kpis.utilizacaoEfetiva.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Utilização Efectiva' : 'Effective Utilization',
            definition: language === 'pt' 
              ? 'Percentagem do tempo disponível em que a planta está efectivamente a processar minério.'
              : 'Percentage of available time the plant is effectively processing ore.',
            relevance: language === 'pt'
              ? 'Mede eficiência operacional. Gap vs disponibilidade indica perdas por falta de alimentação, energia ou outros factores.'
              : 'Measures operational efficiency. Gap vs availability indicates losses from feed shortage, power or other factors.',
            currentReading: language === 'pt'
              ? `Utilização de ${data.kpis.utilizacaoEfetiva.actual.toFixed(1)}% vs ${data.kpis.disponibilidadePlanta.actual.toFixed(1)}% disponibilidade. Gap de ${(data.kpis.disponibilidadePlanta.actual - data.kpis.utilizacaoEfetiva.actual).toFixed(1)}pp a investigar.`
              : `Utilization of ${data.kpis.utilizacaoEfetiva.actual.toFixed(1)}% vs ${data.kpis.disponibilidadePlanta.actual.toFixed(1)}% availability. Gap of ${(data.kpis.disponibilidadePlanta.actual - data.kpis.utilizacaoEfetiva.actual).toFixed(1)}pp to investigate.`,
            drillDown: [
              { label: language === 'pt' ? 'Falta de Alimentação' : 'Feed Shortage', value: '4.2%' },
              { label: language === 'pt' ? 'Cortes de Energia' : 'Power Cuts', value: '2.8%' },
              { label: language === 'pt' ? 'Outras Paragens' : 'Other Stoppages', value: '2.7%' },
            ]
          }}
        />
        <KPICardOperations 
          label={language === 'pt' ? 'Horas Paragem Planta' : 'Plant Downtime Hours'}
          value={data.kpis.horasParagemPlanta.actual}
          unit={data.kpis.horasParagemPlanta.unit}
          vsBudget={data.kpis.horasParagemPlanta.variance}
          vsPrior={data.kpis.horasParagemPlanta.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          inverse={true}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Horas de Paragem' : 'Downtime Hours',
            definition: language === 'pt' 
              ? 'Total de horas em que a planta esteve parada por avaria, manutenção ou factores externos.'
              : 'Total hours the plant was stopped due to breakdown, maintenance or external factors.',
            relevance: language === 'pt'
              ? 'Cada hora de paragem representa ~35K USD em receita perdida. Análise de causas raíz é crítica.'
              : 'Each downtime hour represents ~35K USD in lost revenue. Root cause analysis is critical.',
            currentReading: language === 'pt'
              ? `${data.kpis.horasParagemPlanta.actual} horas de paragem (${data.kpis.horasParagemPlanta.variance > 0 ? '+' : ''}${data.kpis.horasParagemPlanta.variance}% vs orçamento). Impacto estimado: ${Math.round(data.kpis.horasParagemPlanta.actual * 35 / 1000)}M USD.`
              : `${data.kpis.horasParagemPlanta.actual} downtime hours (${data.kpis.horasParagemPlanta.variance > 0 ? '+' : ''}${data.kpis.horasParagemPlanta.variance}% vs budget). Estimated impact: ${Math.round(data.kpis.horasParagemPlanta.actual * 35 / 1000)}M USD.`,
            drillDown: [
              { label: language === 'pt' ? 'Manutenção Planeada' : 'Planned Maintenance', value: '95 hrs', variance: -5.0 },
              { label: language === 'pt' ? 'Avarias' : 'Breakdowns', value: '68 hrs', variance: 15.2 },
              { label: language === 'pt' ? 'Factores Externos' : 'External Factors', value: '52 hrs', variance: 8.5 },
            ]
          }}
        />
        <KPICardOperations 
          label={language === 'pt' ? 'Taxa Transf. DMS' : 'DMS Transfer Rate'}
          value={data.kpis.taxaTransferenciaDMS.actual}
          unit={data.kpis.taxaTransferenciaDMS.unit}
          vsBudget={data.kpis.taxaTransferenciaDMS.variance}
          vsPrior={data.kpis.taxaTransferenciaDMS.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Taxa de Transferência DMS' : 'DMS Transfer Rate',
            definition: language === 'pt' 
              ? 'Volume de material transferido para o Dense Media Separator (DMS) por hora de operação.'
              : 'Volume of material transferred to the Dense Media Separator (DMS) per operating hour.',
            relevance: language === 'pt'
              ? 'Indicador de throughput da etapa crítica de concentração. Impacta directamente a capacidade de recuperação.'
              : 'Throughput indicator of critical concentration stage. Directly impacts recovery capacity.',
            currentReading: language === 'pt'
              ? `Taxa de ${data.kpis.taxaTransferenciaDMS.actual} t/h (${data.kpis.taxaTransferenciaDMS.variance > 0 ? '+' : ''}${data.kpis.taxaTransferenciaDMS.variance}% vs orçamento).`
              : `Rate of ${data.kpis.taxaTransferenciaDMS.actual} t/h (${data.kpis.taxaTransferenciaDMS.variance > 0 ? '+' : ''}${data.kpis.taxaTransferenciaDMS.variance}% vs budget).`,
            drillDown: [
              { label: 'DMS Linha 1', value: '112 t/h', variance: 2.5 },
              { label: 'DMS Linha 2', value: '105 t/h', variance: -4.2 },
              { label: language === 'pt' ? 'Capacidade Máx.' : 'Max Capacity', value: '125 t/h' },
            ]
          }}
        />
      </div>

      {/* ============ REAL VS TRAJECTÓRIA DO ORÇAMENTO ============ */}
      {trajectoryKpisYear.length > 0 && (
        <RealVsBudgetTrajectory 
          year={year}
          kpis={trajectoryKpisYear}
          defaultKpi="disponibilidade"
        />
      )}

      {/* ============ SUMÁRIO EXECUTIVO - Formato Demonstração de Resultados ============ */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Sumário Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Eficiência operacional' : 'Operational efficiency'}
        language={language}
        maxHeight="500px"
        icon={Gauge}
        dataTestId="operational-efficiency-executive-summary"
      >
        {/* Executive Overview */}
        <div className="mb-6">
          <p className="text-slate-700 leading-relaxed text-sm mb-4">
            {language === 'pt' 
              ? `Esta página acompanha a fiabilidade operacional dos circuitos críticos. A leitura deve combinar disponibilidade, utilização, tempo perdido e capacidade real de conversão. O CFO deve perceber se a operação está a perder desempenho por indisponibilidade, subutilização ou falhas recorrentes.`
              : `This page tracks the operational reliability of critical circuits. The reading should combine availability, utilization, lost time and real conversion capacity. The CFO should understand if operations are losing performance due to unavailability, underutilization or recurring failures.`}
          </p>
          <p className="text-slate-700 leading-relaxed text-sm">
            {language === 'pt' 
              ? `O ${data.period} mostra disponibilidade de ${data.kpis.disponibilidadePlanta.actual}% (${data.kpis.disponibilidadePlanta.variance > 0 ? '+' : ''}${data.kpis.disponibilidadePlanta.variance}% vs plano), utilização efectiva de ${data.kpis.utilizacaoEfetiva.actual}% e ${data.kpis.horasParagemPlanta.actual} horas de paragem. Os desvios podem não se reflectir imediatamente em produção, mas aumentam risco sobre continuidade operacional, custo unitário e cumprimento do plano.`
              : `${data.period} shows availability of ${data.kpis.disponibilidadePlanta.actual}% (${data.kpis.disponibilidadePlanta.variance > 0 ? '+' : ''}${data.kpis.disponibilidadePlanta.variance}% vs plan), effective utilization of ${data.kpis.utilizacaoEfetiva.actual}% and ${data.kpis.horasParagemPlanta.actual} downtime hours. Deviations may not immediately reflect in production, but increase risk on operational continuity, unit cost and plan fulfillment.`}
          </p>
        </div>

        {/* Driver Sections */}
        <div className="space-y-4">
          {/* Disponibilidade e Utilização */}
          <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-5 border border-blue-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Gauge className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Disponibilidade e Utilização' : 'Availability and Utilization'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Os activos críticos estão disponíveis e efectivamente utilizados?' : 'Are critical assets available and effectively utilized?'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed">{data.disponibilidadeUtilizacao.leituraExecutiva[language]}</p>
          </div>

          {/* Paragens e Tempo Perdido */}
          <div className="bg-gradient-to-r from-amber-50 to-slate-50 rounded-xl p-5 border border-amber-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Paragens e Tempo Perdido' : 'Shutdowns and Lost Time'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Onde é que a operação está a perder capacidade útil?' : 'Where is operation losing useful capacity?'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed">{data.paragensTempoPerdido.leituraExecutiva[language]}</p>
          </div>

          {/* Eficiência do Circuito - 2 cards lado a lado */}
          <div className="grid grid-cols-2 gap-4">
            {/* Utilização Efectiva */}
            <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-4 border border-blue-100">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Gauge className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-primary text-xs">{language === 'pt' ? 'Utilização Efectiva' : 'Effective Utilization'}</h4>
                  <p className="text-[10px] text-slate-500">{language === 'pt' ? 'Capacidade convertida em operação útil?' : 'Capacity converted to useful operation?'}</p>
                </div>
              </div>
              <p className="text-slate-600 text-[11px] leading-relaxed">
                {language === 'pt' 
                  ? `O 1T 2026 registou utilização efectiva de ${data.utilizacaoEfetivaCircuito?.ytd?.real || data.kpis.utilizacaoEfetiva.actual}% vs ${data.utilizacaoEfetivaCircuito?.ytd?.orc || 88}% orçamentado. A lacuna indica capacidade instalada não convertida em produção útil.`
                  : `Q1 2026 recorded effective utilization of ${data.utilizacaoEfetivaCircuito?.ytd?.real || data.kpis.utilizacaoEfetiva.actual}% vs ${data.utilizacaoEfetivaCircuito?.ytd?.orc || 88}% budgeted. The gap indicates installed capacity not converted to useful production.`}
              </p>
            </div>
            
            {/* Taxa de Transferência DMS */}
            <div className="bg-gradient-to-r from-emerald-50 to-slate-50 rounded-xl p-4 border border-emerald-100">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Activity className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-primary text-xs">{language === 'pt' ? 'Taxa de Transferência DMS' : 'DMS Transfer Rate'}</h4>
                  <p className="text-[10px] text-slate-500">{language === 'pt' ? 'Circuito a transferir ao ritmo esperado?' : 'Circuit transferring at expected rate?'}</p>
                </div>
              </div>
              <p className="text-slate-600 text-[11px] leading-relaxed">
                {language === 'pt' 
                  ? `A taxa de transferência DMS foi de ${data.taxaTransferenciaDMS?.ytd?.real || 145} m³/h vs ${data.taxaTransferenciaDMS?.ytd?.orc || 150} m³/h orçamentado. O circuito opera ligeiramente abaixo da capacidade nominal.`
                  : `DMS transfer rate was ${data.taxaTransferenciaDMS?.ytd?.real || 145} m³/h vs ${data.taxaTransferenciaDMS?.ytd?.orc || 150} m³/h budgeted. Circuit operates slightly below nominal capacity.`}
              </p>
            </div>
          </div>
        </div>
      </ExecutiveSummarySection>

      {/* ============ BLOCO 1: DISPONIBILIDADE E UTILIZAÇÃO DOS CIRCUITOS CRÍTICOS ============ */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Gauge className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800 text-sm">{language === 'pt' ? 'Disponibilidade e Utilização dos Circuitos Críticos' : 'Critical Circuit Availability and Utilization'}</h3>
            <p className="text-[10px] text-slate-500">{language === 'pt' ? 'Os activos críticos estão disponíveis e efectivamente utilizados ao nível necessário?' : 'Are critical assets available and effectively utilized at the required level?'}</p>
          </div>
        </div>
        
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left py-2 px-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Circuito' : 'Circuit'}</th>
                <th className="text-center py-2 px-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Disponibilidade' : 'Availability'}</th>
                <th className="text-center py-2 px-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Utilização' : 'Utilization'}</th>
                <th className="text-center py-2 px-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Util. Efectiva' : 'Eff. Util.'}</th>
                <th className="text-center py-2 px-2 font-semibold text-slate-500 uppercase tracking-wider text-[10px] w-24">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.disponibilidadeUtilizacao.circuitos.map((item, idx) => (
                <tr key={idx} className={`border-t border-slate-100 ${idx % 2 === 0 ? 'bg-slate-50/30' : ''}`}>
                  <td className="py-2.5 px-3">
                    <span className="text-slate-700 font-medium">{item.nome}</span>
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <span className="font-bold text-slate-800">{item.disponibilidade}%</span>
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <span className="font-bold text-slate-800">{item.utilizacao}%</span>
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <span className={`font-bold ${item.utilizacaoEfetiva >= 80 ? 'text-emerald-600' : item.utilizacaoEfetiva >= 75 ? 'text-amber-600' : 'text-rose-600'}`}>
                      {item.utilizacaoEfetiva}%
                    </span>
                  </td>
                  <td className="py-2.5 px-2 text-center">
                    <StatusBadge status={item.status} language={language} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-slate-400 mt-2.5 flex items-center gap-1">
          <FileText className="w-3 h-3" />
          {language === 'pt' ? 'Fonte: Relatórios Mensais de Produção e Metalurgia' : 'Source: Monthly Production and Metallurgy Reports'}
        </p>
      </div>

      {/* ============ BLOCO 2: PARAGENS E TEMPO PERDIDO ============ */}
      {is2026 && data.paragensTempoPerdido?.monthly && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
              <Clock className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 text-sm">{language === 'pt' ? 'Paragens e Tempo Perdido' : 'Shutdowns and Lost Time'}</h3>
              <p className="text-[10px] text-slate-500">{language === 'pt' ? 'Onde é que a operação está a perder capacidade útil?' : 'Where is operation losing useful capacity?'}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-6">
            {/* Gráfico de Paragens Mensais */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-slate-700 text-xs">{language === 'pt' ? 'Paragens Mensais' : 'Monthly Shutdowns'}</h4>
                  <p className="text-[10px] text-slate-400">{language === 'pt' ? 'Planeadas vs Não Planeadas' : 'Planned vs Unplanned'}</p>
                </div>
                <div className="flex items-center gap-3 text-[10px]">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-emerald-500"></div>
                    <span className="text-slate-600">{language === 'pt' ? 'Planeadas' : 'Planned'}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-rose-500"></div>
                    <span className="text-slate-600">{language === 'pt' ? 'Não Planeadas' : 'Unplanned'}</span>
                  </div>
                </div>
              </div>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.paragensTempoPerdido.monthly} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 10 }} />
                    <YAxis 
                      tick={{ fill: '#64748b', fontSize: 10 }} 
                      label={{ value: 'hrs', angle: -90, position: 'insideLeft', style: { fontSize: 9, fill: '#94a3b8' } }}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#0A192F', border: 'none', borderRadius: '8px', fontSize: '11px' }}
                      labelStyle={{ color: '#C5A059' }}
                    />
                    <Bar dataKey="paragensPlaneadas" name={language === 'pt' ? 'Planeadas' : 'Planned'} fill="#10b981" stackId="a" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="paragensNaoPlaneadas" name={language === 'pt' ? 'Não Planeadas' : 'Unplanned'} fill="#f43f5e" stackId="a" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* YTD Summary */}
              <div className="mt-3 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-500 font-medium">{language === 'pt' ? 'Total YTD:' : 'YTD Total:'}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-700">
                      {data.paragensTempoPerdido.ytd.total.real} hrs
                    </span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${data.paragensTempoPerdido.ytd.total.variance <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                      {data.paragensTempoPerdido.ytd.total.variance > 0 ? '+' : ''}{data.paragensTempoPerdido.ytd.total.variance}% vs orç.
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Principais Causas de Paragem */}
            <div>
              <h4 className="font-semibold text-slate-700 text-xs mb-3">{language === 'pt' ? 'Principais Causas de Paragem' : 'Main Shutdown Causes'}</h4>
              <div className="space-y-2">
                {data.paragensTempoPerdido.principaisCausas.map((causa, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] text-slate-700">{causa.causa[language]}</span>
                        <span className="text-[10px] font-semibold text-slate-600">{causa.horas} hrs ({causa.percentagem}%)</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${idx === 0 ? 'bg-rose-500' : idx === 1 ? 'bg-amber-500' : 'bg-slate-400'}`}
                          style={{ width: `${causa.percentagem}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <p className="text-[10px] text-slate-400 mt-4 flex items-center gap-1">
            <FileText className="w-3 h-3" />
            {language === 'pt' ? 'Fonte: Relatórios Mensais de Manutenção e Produção' : 'Source: Monthly Maintenance and Production Reports'}
          </p>
        </div>
      )}

      {/* ============ BLOCO 3: EFICIÊNCIA DO CIRCUITO - 2 PAINÉIS LADO A LADO ============ */}
      {is2026 && data.utilizacaoEfetivaCircuito?.monthly && data.taxaTransferenciaDMS?.monthly && (
        <div className="grid grid-cols-2 gap-5">
          {/* PAINEL 1: Utilização Efectiva do Circuito */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                <Gauge className="w-3.5 h-3.5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800 text-sm">{language === 'pt' ? 'Utilização Efectiva do Circuito' : 'Effective Circuit Utilization'}</h3>
                <p className="text-[9px] text-slate-500">{language === 'pt' ? 'A capacidade disponível está a ser convertida em operação útil?' : 'Is available capacity being converted to useful operation?'}</p>
              </div>
            </div>
            
            {/* 3 Cards compactos */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100 text-center">
                <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-0.5">{language === 'pt' ? 'Real' : 'Actual'}</p>
                <p className="text-lg font-bold text-slate-800">{data.utilizacaoEfetivaCircuito.ytd.real}<span className="text-xs font-normal text-slate-500">%</span></p>
              </div>
              <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100 text-center">
                <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-0.5">{language === 'pt' ? 'Orçamento' : 'Budget'}</p>
                <p className="text-lg font-bold text-slate-600">{data.utilizacaoEfetivaCircuito.ytd.orc}<span className="text-xs font-normal text-slate-500">%</span></p>
              </div>
              <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100 text-center">
                <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-0.5">{language === 'pt' ? 'Variação' : 'Variance'}</p>
                <p className={`text-lg font-bold ${data.utilizacaoEfetivaCircuito.ytd.variance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {data.utilizacaoEfetivaCircuito.ytd.variance > 0 ? '+' : ''}{data.utilizacaoEfetivaCircuito.ytd.variance}%
                </p>
              </div>
            </div>
            
            {/* Gráfico */}
            <div className="h-32">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.utilizacaoEfetivaCircuito.monthly} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 9 }} />
                  <YAxis 
                    domain={[70, 95]}
                    tick={{ fill: '#64748b', fontSize: 9 }} 
                    tickFormatter={(v) => `${v}%`}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#0A192F', border: 'none', borderRadius: '8px', fontSize: '10px' }}
                    labelStyle={{ color: '#C5A059' }}
                    formatter={(value) => [`${value}%`, '']}
                  />
                  <Bar dataKey="utilizReal" name={language === 'pt' ? 'Real' : 'Actual'} fill="#3b82f6" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="utilizOrc" name={language === 'pt' ? 'Orçamento' : 'Budget'} fill="#3b82f640" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Definição compacta */}
            <div className="mt-2 p-2 bg-slate-50 rounded border border-slate-100">
              <p className="text-[9px] text-slate-500">
                <span className="font-semibold text-slate-600">{language === 'pt' ? 'Fórmula:' : 'Formula:'}</span>{' '}
                {language === 'pt' 
                  ? 'Utilização Efectiva = Disponibilidade × Utilização. Mede a % de capacidade instalada convertida em operação útil.'
                  : 'Effective Utilization = Availability × Utilization. Measures % of installed capacity converted to useful operation.'}
              </p>
            </div>
            
            <p className="text-[9px] text-slate-400 mt-2 flex items-center gap-1">
              <FileText className="w-2.5 h-2.5" />
              {language === 'pt' ? 'Fonte: Relatórios Mensais de Produção' : 'Source: Monthly Production Reports'}
            </p>
          </div>

          {/* PAINEL 2: Taxa de Transferência DMS */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
                <Activity className="w-3.5 h-3.5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800 text-sm">{language === 'pt' ? 'Taxa de Transferência DMS' : 'DMS Transfer Rate'}</h3>
                <p className="text-[9px] text-slate-500">{language === 'pt' ? 'O circuito está a transferir volume ao ritmo esperado?' : 'Is the circuit transferring volume at the expected rate?'}</p>
              </div>
            </div>
            
            {/* 3 Cards compactos */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100 text-center">
                <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-0.5">{language === 'pt' ? 'Real' : 'Actual'}</p>
                <p className="text-lg font-bold text-slate-800">{data.taxaTransferenciaDMS.ytd.real}<span className="text-xs font-normal text-slate-500 ml-0.5">m³/h</span></p>
              </div>
              <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100 text-center">
                <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-0.5">{language === 'pt' ? 'Orçamento' : 'Budget'}</p>
                <p className="text-lg font-bold text-slate-600">{data.taxaTransferenciaDMS.ytd.orc}<span className="text-xs font-normal text-slate-500 ml-0.5">m³/h</span></p>
              </div>
              <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100 text-center">
                <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-0.5">{language === 'pt' ? 'Variação' : 'Variance'}</p>
                <p className={`text-lg font-bold ${data.taxaTransferenciaDMS.ytd.variance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {data.taxaTransferenciaDMS.ytd.variance > 0 ? '+' : ''}{data.taxaTransferenciaDMS.ytd.variance}%
                </p>
              </div>
            </div>
            
            {/* Gráfico */}
            <div className="h-32">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.taxaTransferenciaDMS.monthly} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 9 }} />
                  <YAxis 
                    domain={[130, 160]}
                    tick={{ fill: '#64748b', fontSize: 9 }} 
                    tickFormatter={(v) => `${v}`}
                    label={{ value: 'm³/h', angle: -90, position: 'insideLeft', style: { fontSize: 8, fill: '#94a3b8' } }}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#0A192F', border: 'none', borderRadius: '8px', fontSize: '10px' }}
                    labelStyle={{ color: '#C5A059' }}
                    formatter={(value) => [`${value} m³/h`, '']}
                  />
                  <Bar dataKey="taxaReal" name={language === 'pt' ? 'Real' : 'Actual'} fill="#10b981" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="taxaOrc" name={language === 'pt' ? 'Orçamento' : 'Budget'} fill="#10b98140" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Definição compacta */}
            <div className="mt-2 p-2 bg-slate-50 rounded border border-slate-100">
              <p className="text-[9px] text-slate-500">
                <span className="font-semibold text-slate-600">{language === 'pt' ? 'Fórmula:' : 'Formula:'}</span>{' '}
                {language === 'pt' 
                  ? 'Taxa de Transferência = Volume processado DMS ÷ Horas de operação. Mede o ritmo de processamento do circuito.'
                  : 'Transfer Rate = DMS processed volume ÷ Operating hours. Measures circuit processing rate.'}
              </p>
            </div>
            
            <p className="text-[9px] text-slate-400 mt-2 flex items-center gap-1">
              <FileText className="w-2.5 h-2.5" />
              {language === 'pt' ? 'Fonte: Relatórios Mensais de Metalurgia — Secção DMS' : 'Source: Monthly Metallurgy Reports — DMS Section'}
            </p>
          </div>
        </div>
      )}

      {/* ============ BLOCO 4: PRINCIPAIS CONSTRANGIMENTOS OPERACIONAIS ============ */}
      {is2026 && data.constrangimentos && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 text-sm">{language === 'pt' ? 'Principais Constrangimentos Operacionais' : 'Main Operational Constraints'}</h3>
              <p className="text-[10px] text-slate-500">{language === 'pt' ? 'O que está realmente a travar a eficiência operacional?' : 'What is really hindering operational efficiency?'}</p>
            </div>
          </div>
          
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left py-2 px-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Facto Operacional' : 'Operational Fact'}</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Efeito na Operação' : 'Operational Effect'}</th>
                  <th className="text-center py-2 px-2 font-semibold text-slate-500 uppercase tracking-wider text-[10px] w-20">{language === 'pt' ? 'Impacto' : 'Impact'}</th>
                </tr>
              </thead>
              <tbody>
                {data.constrangimentos.map((item, idx) => (
                  <tr key={idx} className={`border-t border-slate-100 ${idx % 2 === 0 ? 'bg-slate-50/30' : ''}`}>
                    <td className="py-2.5 px-3">
                      <span className="text-slate-700">{item.facto[language]}</span>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="text-slate-600">{item.efeito[language]}</span>
                    </td>
                    <td className="py-2.5 px-2 text-center">
                      <SeverityBadge severity={item.severidade} language={language} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-slate-400 mt-2.5 flex items-center gap-1">
            <FileText className="w-3 h-3" />
            {language === 'pt' ? 'Fonte: Relatórios Mensais de Manutenção e reuniões de acompanhamento operacional' : 'Source: Monthly Maintenance Reports and operational follow-up meetings'}
          </p>
        </div>
      )}

      {/* ============ BLOCO 5: PONTE OPERAÇÃO → LEITURA FINANCEIRA (qualitativa) ============ */}
      <div className="bg-gradient-to-br from-[#0A192F] to-[#112240] rounded-xl p-5">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-lg bg-gold-500/20 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-gold-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm">{language === 'pt' ? 'Ponte Operação → Leitura Financeira' : 'Operations → Financial Reading Bridge'}</h3>
            <p className="text-[10px] text-slate-400">{language === 'pt' ? 'Tradução qualitativa dos factos operacionais em risco sobre execução' : 'Qualitative translation of operational facts to execution risk'}</p>
          </div>
        </div>
        
        <div className="overflow-hidden rounded-lg border border-white/10">
          <table className="w-full">
            <thead>
              <tr className="bg-white/5">
                <th className="text-left py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{language === 'pt' ? 'Facto Operacional' : 'Operational Fact'}</th>
                <th className="text-left py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{language === 'pt' ? 'Implicação Operacional' : 'Operational Implication'}</th>
                <th className="text-left py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{language === 'pt' ? 'Leitura Financeira' : 'Financial Reading'}</th>
                <th className="text-center py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider w-20">{language === 'pt' ? 'Impacto' : 'Impact'}</th>
              </tr>
            </thead>
            <tbody>
              {data.ponteFinanceira.map((item, idx) => (
                <tr key={idx} className={`border-t border-white/5 ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                  <td className="py-2.5 px-3">
                    <span className={`text-xs font-medium ${
                      item.severidade === 'low' ? 'text-emerald-400' :
                      item.severidade === 'medium' ? 'text-amber-400' :
                      'text-rose-400'
                    }`}>
                      {item.facto[language]}
                    </span>
                  </td>
                  <td className="py-2.5 px-3">
                    <span className="text-[11px] text-slate-300">{item.implicacaoOperacional[language]}</span>
                  </td>
                  <td className="py-2.5 px-3">
                    <span className="text-[11px] text-slate-300">{item.leituraFinanceira[language]}</span>
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                      item.severidade === 'low' ? 'bg-emerald-500/20 text-emerald-400' :
                      item.severidade === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-rose-500/20 text-rose-400'
                    }`}>
                      {item.severidade === 'low' ? (language === 'pt' ? 'Limitado' : 'Limited') :
                       item.severidade === 'medium' ? (language === 'pt' ? 'Moderado' : 'Moderate') :
                       (language === 'pt' ? 'Relevante' : 'Relevant')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-slate-500 mt-3 flex items-center gap-1">
          <FileText className="w-3 h-3" />
          {language === 'pt' ? 'Nota: Leitura qualitativa — quantificação financeira requer modelo de custos e análise dedicada' : 'Note: Qualitative reading — financial quantification requires cost model and dedicated analysis'}
        </p>
      </div>
    </div>
  );
};

export default OperationalEfficiency;
